import { Menu, MenuItem } from '@mui/material';
import { styled } from '@mui/material/styles';
import { InfoCircleFill } from '@styled-icons/bootstrap';
import { ICellRendererParams } from 'ag-grid-community';
import { createEmptyRow, isRowEmpty } from 'FeatureModules/DataImport/utils/dataImport.utils';
import Divider from 'DesignSystem/Layout/Divider/Divider';
import Icon from 'DesignSystem/Primitives/Icon/Icon';
import React, { FC, ReactNode, useRef, useState } from 'react';
import { Booleanish, Tbooleanish } from 'types/booleanish.types';
import copyTextToClipboard, { pasteTextFromClipboard } from 'utils/clipboard.utils';
import { IExcelTableContext } from '../excelTable.types';
import FlexBox from 'Components/FlexBox';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
interface IValidatedCellRendererParams extends ICellRendererParams<any, any, IExcelTableContext> {
  children?: ReactNode;
}

const ValidatorContainer = styled('div')<{ focus: Tbooleanish }>(({ theme, focus }) => ({
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  background: focus === 'true' ? theme.custom.themeColors.primary[5] : 'inherit',
}));

const CellContent = styled('div')(() => ({
  overflowX: 'hidden',
  textOverflow: 'ellipsis',
  width: '100%',
}));

const InfoIcon = styled(InfoCircleFill)(() => ({
  marginLeft: '5px',
  minWidth: '10px',
}));

const StyledMenu = styled(Menu)(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: 8,
    boxShadow: theme.custom.shadows[1],
  },
  '& .MuiMenu-list': {
    minWidth: 200,
  },
}));

const Tag = styled('div')(({ theme }) => ({
  padding: 4,
  borderRadius: 4,
  height: 16,
  minWidth: 16,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: theme.custom.themeColors.grayScale[20],
  color: theme.custom.themeColors.grayScale[80],
}));

const MenuItemError = styled(MenuItem)(({ theme }) => ({
  color: theme.custom.themeColors.error[100],
}));

const ValidatedCellRenderer: FC<IValidatedCellRendererParams> = ({
  value,
  colDef,
  data,
  context,
  children,
  api,
  setValue,
}) => {
  const [contextMenuOpen, setContextMenuOpen] = useState<boolean>(false);

  const isTableEditable = context.mode === 'edit' || context.mode === 'add-edit';
  const field = colDef?.field;

  const menuRef = useRef<HTMLDivElement>(null);

  const handleCloseContextMenu = () => {
    setContextMenuOpen(false);
  };

  const isValid =
    isRowEmpty(data) || !field || data[field]?.isValid || data[field]?.isValid === undefined;
  const hasMessage =
    data &&
    field &&
    (data[field]?.errorMessage || data[field]?.warningMessage || data[field]?.infoMessage);

  const handleCutClick = () => {
    if (!value) return;
    copyTextToClipboard(value);
    setValue?.(undefined);
    handleCloseContextMenu();
  };

  const handleCopyClick = () => {
    if (!value) return;
    copyTextToClipboard(value);
    handleCloseContextMenu();
  };

  const handlePasteClick = async () => {
    if (context.mode !== 'edit' && context.mode !== 'add-edit') return;
    const clipboardValue = await pasteTextFromClipboard();
    setValue?.(clipboardValue);
    handleCloseContextMenu();
  };

  const handleClearCellClick = () => {
    setValue?.(undefined);
    handleCloseContextMenu();
  };

  const handleClearRowClick = () => {
    const focusedRowIndex = api.getFocusedCell()?.rowIndex;
    if (focusedRowIndex === undefined) {
      return;
    }
    api.applyTransaction({ remove: [data] });
    api.applyTransaction({
      addIndex: focusedRowIndex,
      add: Array.from({ length: 1 }, () => createEmptyRow()),
    });
    handleCloseContextMenu();
  };

  const handleDeleteRowClick = () => {
    api.applyTransaction({ remove: [data] });
    handleCloseContextMenu();
  };

  const handleInsertRowClick = (before?: boolean) => {
    const focusedRowIndex = api.getFocusedCell()?.rowIndex;
    if (focusedRowIndex === undefined) {
      return;
    }
    api.applyTransaction({
      addIndex: focusedRowIndex + (before ? 0 : 1),
      add: Array.from({ length: 1 }, () => createEmptyRow()),
    });
    handleCloseContextMenu();
  };

  const handleUndoClick = () => {
    api.undoCellEditing();
    handleCloseContextMenu();
  };

  const handleRedoClick = () => {
    api.redoCellEditing();
    handleCloseContextMenu();
  };

  return (
    <>
      <ValidatorContainer
        ref={menuRef}
        focus={Booleanish(contextMenuOpen)}
        onContextMenu={e => {
          e.preventDefault();
          setContextMenuOpen(true);
        }}
      >
        <CellContent>{children || <span>{value}</span>}</CellContent>
        {(!isValid || hasMessage) && <InfoIcon size={10} />}
      </ValidatorContainer>

      <StyledMenu
        anchorEl={menuRef.current}
        anchorOrigin={{ vertical: 'center', horizontal: 'center' }}
        open={contextMenuOpen}
        onContextMenu={e => {
          e.preventDefault();
          setContextMenuOpen(false);
        }}
        onClose={() => setContextMenuOpen(false)}
      >
        <MenuItem disabled={!value || !isTableEditable} onClick={handleCutClick}>
          <FlexBox width="100%" justifyContent="space-between">
            <FlexBox>
              <Icon mr={1} name="cut" />
              Cut
            </FlexBox>
            <FlexBox gap={0.2}>
              <Tag>{context.isAppleDevice ? <>&#8984;</> : 'CTRL'}</Tag>+<Tag>X</Tag>
            </FlexBox>
          </FlexBox>
        </MenuItem>

        <MenuItem disabled={!value} onClick={handleCopyClick}>
          <FlexBox width="100%" justifyContent="space-between">
            <FlexBox>
              <Icon mr={1} name="copy" />
              Copy
            </FlexBox>
            <FlexBox gap={0.2}>
              <Tag>{context.isAppleDevice ? <>&#8984;</> : 'CTRL'}</Tag>+<Tag>C</Tag>
            </FlexBox>
          </FlexBox>
        </MenuItem>

        <MenuItem disabled={!isTableEditable} onClick={handlePasteClick}>
          <FlexBox width="100%" justifyContent="space-between">
            <FlexBox>
              <Icon mr={1} name="paste" />
              Paste
            </FlexBox>
            <FlexBox gap={0.2}>
              <Tag>{context.isAppleDevice ? <>&#8984;</> : 'CTRL'}</Tag>+<Tag>V</Tag>
            </FlexBox>
          </FlexBox>
        </MenuItem>

        <MenuItem
          disabled={!isTableEditable || api.getCurrentUndoSize() === 0}
          onClick={handleUndoClick}
        >
          <FlexBox width="100%" justifyContent="space-between">
            <FlexBox>
              <Icon mr={1} name="undo" />
              Undo
            </FlexBox>
            <FlexBox gap={0.2}>
              <Tag>{context.isAppleDevice ? <>&#8984;</> : 'CTRL'}</Tag>+<Tag>Z</Tag>
            </FlexBox>
          </FlexBox>
        </MenuItem>

        <MenuItem
          disabled={!isTableEditable || api.getCurrentRedoSize() === 0}
          onClick={handleRedoClick}
        >
          <FlexBox width="100%" justifyContent="space-between">
            <FlexBox>
              <Icon mr={1} name="redo" />
              Redo
            </FlexBox>
            <FlexBox gap={0.2}>
              <Tag>
                <Icon name="shift" />
              </Tag>
              +<Tag>{context.isAppleDevice ? <>&#8984;</> : 'CTRL'}</Tag>+<Tag>Z</Tag>
            </FlexBox>
          </FlexBox>
        </MenuItem>

        <Divider variant="middle" />
        <MenuItem disabled={!isTableEditable} onClick={() => handleInsertRowClick(true)}>
          <Icon mr={1} name="plus" />
          Insert row above
        </MenuItem>

        <MenuItem disabled={!isTableEditable} onClick={() => handleInsertRowClick(false)}>
          <Icon mr={1} name="plus" />
          Insert row below
        </MenuItem>

        <MenuItemError disabled={!isTableEditable} onClick={handleClearRowClick}>
          <Icon mr={1} color="red" name="cross" />
          Clear row
        </MenuItemError>
        <MenuItemError disabled={!isTableEditable} onClick={handleClearCellClick}>
          <FlexBox width="100%" justifyContent="space-between">
            <FlexBox>
              <Icon mr={1} color="red" name="cross" />
              Clear cell
            </FlexBox>
            <FlexBox gap={0.2}>
              <Tag>Del</Tag>
            </FlexBox>
          </FlexBox>
        </MenuItemError>
        <MenuItemError disabled={!isTableEditable} onClick={handleDeleteRowClick}>
          <Icon mr={1} color="red" name="delete" />
          Delete row
        </MenuItemError>
      </StyledMenu>
    </>
  );
};

export default ValidatedCellRenderer;
